import { useContext, useEffect, useMemo, useState } from "react";
import Card from "../../components/Card";
import { Alert, Button, Modal, Select, Table, Typography, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import * as XLSX from "xlsx";
import { AuthService } from "../../services/AuthService";
import { UserContext } from "../../context/userContext";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";

const { Text } = Typography;
const { Dragger } = Upload;

const FIELDS: { key: string; label: string }[] = [
    { key: "name", label: "Emri" },
    { key: "barcode", label: "Shifra e prodhuesit" },
    { key: "productNo", label: "Numri i katalogut" },
    { key: "description", label: "Pershkrimi" },
    { key: "price", label: "Cmimi shumice" },
    { key: "internalPrice", label: "Cmimi intern" },
    { key: "discountedPrice", label: "Cmimi me zbritje" },
    { key: "quantity", label: "Sasia" },
];



const SheetImport = () => {

    const { user } = useContext(UserContext);

    const navigate = useNavigate();

    const [messageApi, contextHolder] = message.useMessage();

    const [loading, setLoading] = useState(false);
    const [fileName, setFileName] = useState<string>(null);
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    const [columnMap, setColumnMap] = useState<{ [key: string]: string }>({});

    const [brands, setBrands] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [stores, setStores] = useState<any[]>([]);

    // Prefilled and locked for the whole import batch - not chosen per-row from the sheet
    const [fixedFields, setFixedFields] = useState({
        categoryId: null,
        brandId: null,
        storeId: null,
    });

    const getBrands = async () => {
        let res = await AuthService.getBrands({});
        if (res?.items && res?.items.length > 0) {
            setBrands(res.items);
            setFixedFields((prev) => ({ ...prev, brandId: prev.brandId ?? res.items[0]?.brandId }));
        }
    };

    const getCategories = async () => {
        let res = await AuthService.getCategories({});
        if (res?.items && res?.items.length > 0) {
            setCategories(res.items);
            setFixedFields((prev) => ({ ...prev, categoryId: prev.categoryId ?? res.items[0]?.categoryId }));
        }
    };

    const getStores = async () => {
        let res = await AuthService.getStores({});
        if (res?.items && res?.items.length > 0) {
            setStores(res.items);
            setFixedFields((prev) => ({ ...prev, storeId: prev.storeId ?? res.items[0]?.storeId }));
        }
    };

    useEffect(() => {
        getBrands();
        getCategories();
        getStores();
    }, []);

    useEffect(() => {
        if (user?.details?.assignedStoreId) {
            setFixedFields((prev) => ({ ...prev, storeId: user.details.assignedStoreId }));
        }
    }, [user?.details?.assignedStoreId]);

    const parseFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const json: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

                if (!json.length) {
                    messageApi.open({ type: "error", content: "Skedari eshte bosh" });
                    return;
                }

                const [headerRow, ...dataRows] = json;
                setHeaders(headerRow.map((h: any) => String(h)));
                setRows(dataRows.filter((row: any[]) => row.some((cell) => cell !== "")));
                setColumnMap({});
                setFileName(file.name);
            } catch (err) {
                console.log("parse error", err);
                messageApi.open({ type: "error", content: "Skedari nuk mund te lexohet" });
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const uploadProps: UploadProps = {
        name: "file",
        multiple: false,
        maxCount: 1,
        accept: ".xlsx,.xls",
        showUploadList: fileName ? { showRemoveIcon: true } : false,
        beforeUpload: (file) => {
            const isExcel =
                file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                file.type === "application/vnd.ms-excel" ||
                file.name.endsWith(".xlsx") ||
                file.name.endsWith(".xls");

            if (!isExcel) {
                messageApi.open({ type: "error", content: "Vetem skedaret Excel (.xlsx, .xls) lejohen" });
                return Upload.LIST_IGNORE;
            }

            parseFile(file);
            return false;
        },
        onRemove: () => {
            setFileName(null);
            setHeaders([]);
            setRows([]);
            setColumnMap({});
        },
    };

    const isMappingComplete = useMemo(() => {
        return FIELDS.every((f) => f.key === "quantity" || !!columnMap[f.key]);
    }, [columnMap]);

    const buildRecord = (row: any[]) => {
        const record: any = { ...fixedFields };
        FIELDS.forEach((f) => {
            const colIndex = headers.indexOf(columnMap[f.key]);
            if (colIndex === -1) {
                record[f.key] = f.key === "quantity" ? 1 : undefined;
            } else {
                record[f.key] = row[colIndex];
            }
        });
        return record;
    };

    const previewColumns = useMemo(() => {
        const fixedColumns = [
            {
                title: "Kategoria", dataIndex: "categoryId", key: "categoryId",
                render: (id: any) => categories.find((c) => c.categoryId === id)?.categoryName ?? "-",
            },
            {
                title: "Brendi", dataIndex: "brandId", key: "brandId",
                render: (id: any) => brands.find((b) => b.brandId === id)?.name ?? "-",
            },
            {
                title: "Depoja", dataIndex: "storeId", key: "storeId",
                render: (id: any) => stores.find((s) => s.storeId === id)?.storeName ?? "-",
            },
        ];
        const mappedColumns = FIELDS.filter((f) => columnMap[f.key]).map((f) => ({
            title: f.label,
            dataIndex: f.key,
            key: f.key,
        }));
        return [...fixedColumns, ...mappedColumns];
    }, [columnMap, categories, brands, stores]);

    const previewData = useMemo(() => {
        if (!isMappingComplete) return [];
        return rows.slice(0, 10).map((row, idx) => ({ key: idx, ...buildRecord(row) }));
    }, [rows, headers, columnMap, isMappingComplete, fixedFields]);

    const submit = () => {
        if (!isMappingComplete) {
            messageApi.open({ type: "error", content: "Zgjidhni nje kolone per te gjitha fushat" });
            return;
        }

        if (!fixedFields.categoryId || !fixedFields.brandId || !fixedFields.storeId) {
            messageApi.open({ type: "error", content: "Kategoria, brendi dhe depoja jane te detyrueshme" });
            return;
        }

        Modal.confirm({
            title: "Konfirmo importin",
            content: `Do te shtohen ${rows.length} produkte ne databaze. Ky veprim nuk mund te kthehet mbrapsht.`,
            okText: "Po, importo",
            cancelText: "Anulo",
            okButtonProps: { danger: true },
            onOk: () => doImport(),
        });
    };

    const doImport = async () => {
        const mappedRows = rows.map((row) => buildRecord(row));

        setLoading(true);
        let res = await AuthService.uploadProductsInBulk({ products: mappedRows })
        if (res?.status == 400) {

            message.success(res?.title);
        }

        if (res?.createdCount || res?.updatedCount) {

            message.success("Produktet u insertuan/perditesuan me sukses");
            navigate(`/articles`);
        }
        console.log("res", res);
        // TODO: send mappedRows to the backend once an import endpoint exists
        setLoading(false);
    };

    return (
        <div className="row">
            {contextHolder}
           
            <div className="col-lg-10">
                <Card title="Import Bulk">
                    <div className="row">

                        <div className="col-12">
                            <Dragger {...uploadProps}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Kliko ose terhiqe skedarin Excel ketu</p>
                                <p className="ant-upload-hint">Lejohen vetem skedaret .xlsx dhe .xls</p>
                            </Dragger>
                        </div>

                        {headers.length > 0 && (
                            <>
                                <div className="col-12 mt-4">
                                    <Text strong>Vlerat e fiksuara per te gjithe importin</Text>
                                </div>

                                <div className="col-lg-4 mt-3">
                                    <Text className="d-block">Kategoria</Text>
                                    <Select
                                        style={{ width: "100%" }}
                                        value={fixedFields.categoryId}
                                        options={categories.map((c) => ({ value: c.categoryId, label: c.categoryName }))}
                                        onChange={(value) => {
                                            setFixedFields(prev => ({ ...prev, categoryId: value }))
                                        }}
                                    />
                                </div>

                                <div className="col-lg-4 mt-3">
                                    <Text className="d-block">Brendi</Text>
                                    <Select
                                        style={{ width: "100%" }}
                                        value={fixedFields.brandId}
                                        options={brands.map((b) => ({ value: b.brandId, label: b.name }))}
                                        onChange={(value) => {
                                            setFixedFields(prev => ({ ...prev, brandId: value }))
                                        }}
                                    />
                                </div>

                                <div className="col-lg-4 mt-3">
                                    <Text className="d-block">Depoja</Text>
                                    <Select
                                        style={{ width: "100%" }}
                                        value={fixedFields.storeId}
                                        options={stores.map((s) => ({ value: s.storeId, label: s.storeName }))}
                                        onChange={(value) => {
                                            setFixedFields(prev => ({ ...prev, storeId: value }))
                                        }}
                                    />
                                </div>

                                <div className="col-12 mt-4">
                                    <Text strong>Perputhja e kolonave</Text>
                                </div>

                                {FIELDS.map((field) => (
                                    <div className="col-lg-6 mt-3" key={field.key}>
                                        <Text className="d-block">{field.label} {field.key === "quantity" ? "(opsionale, default 1)" : "*"}</Text>
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Zgjidh kolonen"
                                            value={columnMap[field.key]}
                                            allowClear
                                            onChange={(value) => setColumnMap({ ...columnMap, [field.key]: value })}
                                            options={headers.map((h) => ({ value: h, label: h }))}
                                        />
                                    </div>
                                ))}

                                {isMappingComplete && (
                                    <div className="col-12 mt-4">
                                        <Text strong>Parapamje</Text>
                                        <Table
                                            className="mt-2"
                                            columns={previewColumns}
                                            dataSource={previewData}
                                            pagination={false}
                                            scroll={{ x: true }}
                                            size="small"
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        <div className="col-12 mt-3">
                            <div className="d-flex justify-content-end">
                                <Button loading={loading} disabled={loading || !isMappingComplete} type="primary" onClick={() => submit()}>Perfundo</Button>
                            </div>
                        </div>

                    </div>
                </Card>
            </div>
        </div>
    )
}

export default SheetImport;
